import { Spin } from 'antd';
import React from 'react';

export interface ContentLoadingProps {
  /** 最小高度，默认 240px；表格页可传更小的值避免撑高容器 */
  minHeight?: number | string;
  /** 状态说明。默认「正在加载页面」，与 BillPageLoading 页面态同一句 */
  label?: React.ReactNode;
}

/**
 * 内容区加载 —— 全站唯一的「内容区还是空的」反馈（路由 chunk 未就绪、页签重挂）。
 *
 * ── 为什么不用全屏那套「航迹线性」──────────────────────────────────────
 *   1. AppSplash 是 position:fixed + inset:0 的整屏层，放进内容区会连侧边栏、
 *      页签栏一起盖住（CLAUDE.md 专门记过这个坑）；
 *   2. 它是为「不知道还要等多久」设计的 11s 航程：表格取数通常几百毫秒，
 *      截断在那一刻船几乎没起步、航迹才几像素，看着更像卡住；
 *   3. 侧边栏和页签栏都还在，用户已经知道自己在哪个页面 —— 这里只需要一个安静的等待提示。
 *
 * ── 为什么这里配文案、表格体里不配 ─────────────────────────────────────
 *   这里整块内容区是空的，用户看不出「将要出现什么」，所以需要一句话兜住；
 *   表格体里表头与列名已经渲染出来了，再往中间贴一行字是重复信息
 *   （表格 loading 只放大图标、不配文案，见 global.less）。
 *
 * ── 规格 ──────────────────────────────────────────────────────────────
 *   图标用 antd size="large"（32px），全站内容区加载统一这一个尺寸；
 *   文案 13px 次要色，与图标留 12px 间距，靠留白而不是动效撑起「大气」。
 */
export const ContentLoading: React.FC<ContentLoadingProps> = ({
  minHeight = 240,
  label = '正在加载页面',
}) => (
  <output
    aria-live="polite"
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      width: '100%',
      height: '100%',
      minHeight,
      color: 'var(--dashboard-text-secondary, var(--text-2))',
      fontSize: 'var(--font-size-base, 13px)',
    }}
  >
    <Spin size="large" />
    <span>{label}</span>
  </output>
);

export default ContentLoading;